/* eslint-disable react/jsx-indent */
/* eslint-disable indent */
/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { extend, QueryProvider } from '@thd-nucleus/data-sources';
import {
  Alert,
  Button,
  Link,
  Typography,
  DrawerBody,
  DrawerFooter,
} from '@one-thd/sui-atomic-components';
import { useConfigService, ExperienceContext } from '@thd-nucleus/experience-context';
import {
  LocalizationBanners,
  MyStoreButton,
  DeliveryZipcodeButton,
} from '@thd-olt-component-react/project-localization';
import { SearchProducts } from './search-products/SearchProducts';
import { SearchResults } from './search-products/SearchResults';
import { CopyListTile } from '../addto/copy-list/CopyListTile';
import { AddToContext } from '../AddToContext';
import { onLocalizationUpdate } from '../AddToHelpers';
import { SCREENS } from '../AddToConstants';
import { StarterListTile, CopyListTileVertical, PastOrdersTile } from './tile-components';
import { AIListBuilder } from './ai-list-builder/AIListBuilder';

// eslint-disable-next-line react/no-unstable-nested-components
const renderTilesSection = ({
  isSearchResults,
  isAiAssistantEnabled,
  isMobile,
  tileWidthClass,
  fsPastOrdersEnabled,
  title,
  materials,
  refType,
  refId,
}) => {
  if (isSearchResults) return null;

  return (
    <>
      {fsPastOrdersEnabled && (
        <div
          data-testid="add-to-options"
          className="sui-flex sui-flex-row sui-gap-2 sui-w-full sui-mt-3"
        >
          <div className={tileWidthClass} style={{ flex: isMobile ? '0 0 50%' : '0 0 33.333%' }}>
            <PastOrdersTile />
          </div>
        </div>
      )}
      {isAiAssistantEnabled ? (
        <>
          <div
            data-testid="add-to-options"
            className="sui-flex sui-flex-row sui-gap-2 sui-w-full sui-mt-3"
          >
            <div className={tileWidthClass} style={{ flex: isMobile ? '0 0 50%' : '0 0 33.333%' }}>
              <CopyListTileVertical />
            </div>
            <div className={tileWidthClass} style={{ flex: isMobile ? '0 0 50%' : '0 0 33.333%' }}>
              <StarterListTile />
            </div>
          </div>
          <div className="sui-mt-4">
            <QueryProvider cacheKey="aiListBuilder">
              <AIListBuilder
                name={title}
                materials={materials}
                refType={refType}
                refId={refId}
              />
            </QueryProvider>
          </div>
        </>
      ) : (
        <>
          <CopyListTile />
        </>
      )}
    </>
  );
};

export const DefaultScreen = ({
  keyword,
  onSearch,
  handleClose,
  projectId,
  selectedGroup,
  selectedGroupId,
  products,
  selectedProject,
  updateList,
  customerType,
  setIsSearchResults,
  isSearchResults = false,
  productPodItemData,
  projectDetails,
  aiListBuilderData,
  isIntent = false,
  handleResolveIntent,
}) => {

  const { MY_STORE, DELIVERY_ZIP } = SCREENS;

  const {
    successMessage,
    setSuccessMessage,
    showSuccessMessage,
    setShowSuccessMessage,
    showErrorMessage,
    setShowErrorMessage,
    addToType,
    setScreen,
    selectedListId,
    selectedListName,
    atcDrawerType,
    setAtcDrawerType
  } = useContext(AddToContext);

  const { projectName, preferredStore, assortmentZip, addresses } = projectDetails || {};
  const assortmentZipId = addresses?.find((address) => address.addressType === 'ASSORTMENT_ZIP')?.id || null;

  const { isConsumerApp } = useContext(ExperienceContext);
  const { channel } = useContext(ExperienceContext);
  const isMobile = channel === 'mobile';
  const showLocalizationComponents = addToType === 'Project' && !isConsumerApp;
  const {
    title = '',
    materials = [],
    refType = 'LIST',
    refId = null,
    onNewIntentsSave = null,
  } = aiListBuilderData || {};


  const fsPastOrdersEnabled = useConfigService('fs:isPastOrdersEnabled');
  const aiListBuilderForB2CEnabled = useConfigService('fs:aiListBuilderForB2CEnabled');

  const isB2B = customerType?.toUpperCase() === 'B2B';
  const isAiAssistantEnabled = !!onNewIntentsSave && (aiListBuilderForB2CEnabled || isB2B);

  const successMessageRef = useRef(null);
  const errorMessageRef = useRef(null);
  const drawerBodyRef = useRef(null);
  const selectedProjectId = projectId || selectedProject?.projectId;
  const selectedProjectName = projectName || selectedProject?.projectName;
  const { searchKeyword, onIntentResolved = null } = productPodItemData || {};
  const isResolvingIntent = Boolean(onIntentResolved);

  const handleClick = () => {
    handleClose();
    window.location.href = `/projects/${selectedProjectId}#group=${selectedGroupId.current}`;
  };

  const isMultipleProducts = products?.length > 1;

  const projectMessage = () => {
    const unassignedGroupMessage = (
      <>
        {isMultipleProducts ? 'Items' : 'Item'} successfully added to&nbsp;
        <Link href={`/projects/${selectedProjectId}`} className="sui-text-sm sui-underline">
          {selectedProjectName}
        </Link>
        &nbsp;
      </>
    );
    const projectMsg = (
      <>
        {isMultipleProducts ? 'Items' : 'Item'} successfully added to&nbsp;
        <Link href={`/projects/${selectedProjectId}`} className="sui-text-sm sui-underline">
          {selectedProjectName}
        </Link>
        &nbsp; in group&nbsp;
        <Link component="button" onClick={handleClick} className="sui-text-sm sui-underline">
          {selectedGroup}
        </Link>
        &nbsp;
      </>
    );
    return selectedGroup === 'Unassigned' ? unassignedGroupMessage : projectMsg;
  };

  const listMessage = () => {
    // Fallback to the simple message if list information is not available
    if (!selectedListId || !selectedListName) {
      return <>Your item has been added to your list.</>;
    }

    return (
      <>
        {isMultipleProducts ? 'Items' : 'Item'} successfully added to&nbsp;
        <Link href={`/list/view/details/${selectedListId}`} className="sui-text-sm sui-underline">
          {selectedListName}
        </Link>
        &nbsp;
      </>
    );
  };

  const handleAlertClose = () => {
    setShowSuccessMessage(false);
    setShowErrorMessage(false);
    setSuccessMessage('');
    if (atcDrawerType) {
      setScreen('MINI_PIP_FULL');
      setAtcDrawerType('');
    }
  };

  // Keyboard handling: Fix layout issues when the virtual keyboard opens/closes.
  // The Visual Viewport API is supported by all major browsers (Chrome, Firefox, Edge, Safari).
  // We detect when the keyboard is closed by comparing viewport height to window height.
  // When closed, we reset the drawer body padding and scroll position to prevent UI elements
  // from being stuck in incorrect positions after keyboard dismissal.
  useEffect(() => {
    if (!window.visualViewport) return; // Visual Viewport API not supported

    const body = drawerBodyRef.current;
    if (!body) return;

    const onResize = () => {
      const keyboardClosed = window.visualViewport.height === window.innerHeight;

      if (keyboardClosed) {
        body.style.paddingBottom = '0px';
        window.scrollTo(0, 0);
      }
    };

    window.visualViewport.addEventListener('resize', onResize);
    // eslint-disable-next-line consistent-return
    return () => window.visualViewport.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (showSuccessMessage) {
      successMessageRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
    if (showErrorMessage) {
      errorMessageRef.current.scrollIntoView({ block: 'end', behavior: 'smooth' });
    }
  }, [showSuccessMessage, showErrorMessage]);

  useEffect(() => {
    if (addToType === 'List') {
      window.LIFE_CYCLE_EVENT_BUS.trigger('add-to.addToMaterialsOverlay', {
        addType: 'list'
      });
    }
  }, []);

  const handleCloseText = !showSuccessMessage || !showErrorMessage ? 'close' : 'ok';
  const tileWidthClass = isMobile ? 'sui-w-1/2' : 'sui-w-1/3';

  return (
    <>
      <DrawerBody ref={drawerBodyRef} style={{ overflowY: 'auto' }} data-testid="add-to-drawer-body-default-screen">
        {successMessage?.length > 0 && (
          <Alert
            status="success"
            onClose={handleAlertClose}
            data-testid="add-to-success-alert"
            ref={successMessageRef}
          >
            {successMessage}
          </Alert>
        )}
        {showSuccessMessage && (
          <Alert
            status="success"
            onClose={handleAlertClose}
            data-testid="add-to-success-alert"
            ref={successMessageRef}
          >
            <div>
              {addToType === 'Project' || atcDrawerType === 'Project'
                ? projectMessage()
                : listMessage()}
            </div>
          </Alert>
        )}
        {showErrorMessage && (
          <Alert
            status="error"
            onClose={() => setShowErrorMessage(false)}
            data-testid="add-to-error-alert"
            ref={errorMessageRef}
          >
            Apologies, a temporary technical issue occured. Please try again soon.
          </Alert>
        )}
        {!products?.length && (
          <>
            {showLocalizationComponents && (
              <LocalizationBanners
                projectId={projectId}
                projectName={projectName}
                assortmentZip={assortmentZip}
                assortmentZipId={assortmentZipId}
                preferredStore={preferredStore}
                onLocalizationUpdate={onLocalizationUpdate}
                removeDrawerLocalStorageValues
              />
            )}
            {/* Main Title */}
            <div className="sui-mb-6">
              <Typography variant="h3" weight="display" align="center">
                What do you need for the job?
              </Typography>
            </div>

            {/* Updated Search Bar */}
            <div className="sui-border-solid sui-border-2 sui-rounded-base sui-border-primary">
              <SearchProducts
                onSubmit={onSearch}
                setIsSearchResults={setIsSearchResults}
                searchKeyword={keyword}
              />
            </div>
            {showLocalizationComponents && (
              <div className="sui-flex sui-pt-4 sui-gap-2 sui-items-center sui-justify-end">
                <DeliveryZipcodeButton
                  assortmentZipCode={assortmentZip}
                  onClick={() => {
                    setScreen(DELIVERY_ZIP);
                  }}
                />
                <MyStoreButton
                  storeId={preferredStore}
                  onClick={() => {
                    setScreen(MY_STORE);
                  }}
                />
              </div>
            )}
            {!isResolvingIntent && renderTilesSection({
              isSearchResults,
              isAiAssistantEnabled,
              isMobile,
              tileWidthClass,
              fsPastOrdersEnabled,
              title,
              materials,
              refType,
              refId,
            })}
            
            {isSearchResults && (
              <SearchResults
                keyword={keyword}
                updateList={updateList}
                productPodItemData={productPodItemData}
                isIntent={isIntent}
                handleResolveIntent={handleResolveIntent}
                isSearchResults={isSearchResults}
                setIsSearchResults={setIsSearchResults}
              />
            )}
          </>
        )}
      </DrawerBody>
      <DrawerFooter position="center" style={{ boxShadow: '0 1px 8px 0 rgb(0 0 0 / 0.375)' }}>
        <Button
          data-testid="add-to-cancel-button"
          variant={!showSuccessMessage && !showErrorMessage ? 'secondary' : 'primary'}
          fullWidth
          onClick={(__evt) => {
            handleClose(__evt, { type: handleCloseText });
          }}
          type="submit"
        >
          {!showSuccessMessage && !showErrorMessage ? 'Cancel' : 'OK'}
        </Button>
      </DrawerFooter>
    </>
  );
};

DefaultScreen.propTypes = {
  keyword: PropTypes.string.isRequired,
  onSearch: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
  projectName: PropTypes.string,
  projectId: PropTypes.string,
  selectedGroup: PropTypes.string,
  selectedGroupId: PropTypes.shape({
    current: PropTypes.string,
  }),
  products: PropTypes.arrayOf(
    PropTypes.shape({
      itemId: PropTypes.string,
      quantity: PropTypes.number,
    })
  ),
  selectedProject: PropTypes.shape({
    projectId: PropTypes.string,
    projectName: PropTypes.string,
  }),
  updateList: PropTypes.func,
  customerType: PropTypes.string,
  setIsSearchResults: PropTypes.func,
  isSearchResults: PropTypes.bool,
  productPodItemData: PropTypes.shape({
    itemId: PropTypes.string,
    quantity: PropTypes.number,
    searchKeyword: PropTypes.string,
  }),
  isIntent: PropTypes.bool,
  handleResolveIntent: PropTypes.func,
  aiListBuilderData: PropTypes.shape({
    onNewIntentsSave: PropTypes.func,
    title: PropTypes.string,
    refType: PropTypes.string,
    refId: PropTypes.string,
    materials: PropTypes.arrayOf(
      PropTypes.shape({
        groupName: PropTypes.string,
        products: PropTypes.arrayOf(
          PropTypes.shape({
            quantity: PropTypes.number,
            product: PropTypes.string,
          })
        ),
      })
    ),
  }),
  projectDetails: PropTypes.shape({
    projectName: PropTypes.string,
    preferredStore: PropTypes.string,
    assortmentZip: PropTypes.string,
    addresses: PropTypes.arrayOf(
      PropTypes.shape({
        addressType: PropTypes.string,
        id: PropTypes.string,
      })
    ),
  }),
};

DefaultScreen.defaultProps = {
  selectedGroup: null,
  selectedGroupId: { current: '' },
  products: [],
  selectedProject: {},
  projectName: null,
  projectId: null,
  updateList: () => {},
  customerType: '',
  setIsSearchResults: () => {},
  isSearchResults: false,
  productPodItemData: {},
  isIntent: false,
  handleResolveIntent: () => {},
  projectDetails: null,
  aiListBuilderData: null,
};

DefaultScreen.dataModel = extend(
  SearchResults,
  LocalizationBanners,
  MyStoreButton,
  CopyListTileVertical
);
